import { useMemo, useCallback } from 'react';
import type { ColumnsOrder } from '@atlassian/jira-business-common';
import { GROUP_BY_PRIORITY } from '@atlassian/jira-business-constants';
import { useViewPreference, COLUMNS_ORDER_PREFERENCE } from '@atlassian/jira-business-preferences';
import { PRIORITY_TYPE } from '@atlassian/jira-platform-field-config';
import { NULL_GROUP_KEY } from '../../common/constants';
import type { BoardColumn } from '../../common/types';
import type { IssueFieldsMap } from '../../services/board-store/types';
import { useGroupBy } from '../group-by';

/** @deprecated use `useGroupsOrder` clean up when removing jwm.board-awesome-enhancements */
export const useColumnsOrder = () => {
	const [groupBy] = useGroupBy();
	const [orderedColumnsByGroupBy, setColumnsOrderPreference] =
		useViewPreference(COLUMNS_ORDER_PREFERENCE);

	const setColumnsOrder = useCallback(
		(newColumnsOrder: BoardColumn[]) => {
			const newColumnsOrderKeys = newColumnsOrder.map((column) => column.groupKey ?? '');
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			setColumnsOrderPreference({
				...orderedColumnsByGroupBy,
				[groupBy]: newColumnsOrderKeys,
			} as ColumnsOrder);
		},
		[groupBy, orderedColumnsByGroupBy, setColumnsOrderPreference],
	);

	const orderedColumns = useMemo(() => {
		if (orderedColumnsByGroupBy && typeof orderedColumnsByGroupBy === 'object') {
			const orderedColumnsKeys = orderedColumnsByGroupBy[groupBy];
			if (Array.isArray(orderedColumnsKeys)) {
				return orderedColumnsKeys;
			}
		}
		return null;
	}, [orderedColumnsByGroupBy, groupBy]);

	return [orderedColumns, setColumnsOrder] as const;
};

/** @deprecated use `useSortGroups` clean up when removing jwm.board-awesome-enhancements */
export const useGetColumnsOrder = () => {
	const [orderedColumnsKeys] = useColumnsOrder();
	const [groupBy] = useGroupBy();

	return useCallback(
		// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
		(columns: BoardColumn[], issueFields: IssueFieldsMap | void) => {
			// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
			let orderedPriorityIds: string[] | void;
			if (groupBy === GROUP_BY_PRIORITY && issueFields) {
				orderedPriorityIds = issueFields[PRIORITY_TYPE]?.fieldConfig?.options?.map(
					(priorityOption) => priorityOption.priorityId,
				);
			}

			const indexInOrder = (groupKey: string, order: string[]) => {
				const index = order.indexOf(groupKey);
				return index >= 0 ? index : columns.length;
			};

			if (orderedColumnsKeys == null) {
				return columns.sort((columnA, columnB) => {
					if (columnA.groupKey === NULL_GROUP_KEY) {
						return -1;
					}
					if (columnB.groupKey === NULL_GROUP_KEY) {
						return 1;
					}
					// Sort priorities based on their order
					if (groupBy === GROUP_BY_PRIORITY && orderedPriorityIds) {
						return (
							indexInOrder(columnA.groupKey ?? '', orderedPriorityIds) -
							indexInOrder(columnB.groupKey ?? '', orderedPriorityIds)
						);
					}
					// Others fields alphabetically
					return columnA.name.localeCompare(columnB.name);
				});
			}

			return columns.sort(
				(columnA, columnB) =>
					indexInOrder(columnA.groupKey ?? '', orderedColumnsKeys) -
					indexInOrder(columnB.groupKey ?? '', orderedColumnsKeys),
			);
		},
		[groupBy, orderedColumnsKeys],
	);
};
